import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { MoowooProvider, useMoowooContext } from './contexts/MoowooContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { CallProvider } from './contexts/CallContext';
import { supabase } from './supabaseClient';

// [초기 진입 필수 화면] 정적 import 유지
import ChatList from './screens/ChatList/ChatList';
import Auth from './screens/Auth/Auth';

import { lazyWithRetry } from './utils/lazyWithRetry';

// [지연 로딩 화면] lazyWithRetry 동적 코드 분할 적용 (청크 로드 실패 시 1회 자동 복구)
const Onboarding = lazyWithRetry(() => import('./screens/Onboarding/Onboarding'));
const GroupChatList = lazyWithRetry(() => import('./screens/GroupChatList/GroupChatList'));
const NewChat = lazyWithRetry(() => import('./screens/NewChat/NewChat'));
const NewGroupChat = lazyWithRetry(() => import('./screens/NewGroupChat/NewGroupChat'));
const ChatConversation = lazyWithRetry(() => import('./screens/ChatConversation/ChatConversation'));
const GroupChatInfo = lazyWithRetry(() => import('./screens/GroupChatInfo/GroupChatInfo'));
const GroupChatInvite = lazyWithRetry(() => import('./screens/GroupChatInvite/GroupChatInvite'));
const Invite = lazyWithRetry(() => import('./screens/Invite/Invite'));
const JoinViaLink = lazyWithRetry(() => import('./screens/JoinViaLink/JoinViaLink'));
const Contacts = lazyWithRetry(() => import('./screens/Contacts/Contacts'));
const FindFriend = lazyWithRetry(() => import('./screens/FindFriend/FindFriend'));
const Profile = lazyWithRetry(() => import('./screens/Profile/Profile'));
const AvatarPicker = lazyWithRetry(() => import('./screens/AvatarPicker/AvatarPicker'));
const FriendProfile = lazyWithRetry(() => import('./screens/FriendProfile/FriendProfile'));
const SetupProfile = lazyWithRetry(() => import('./screens/SetupProfile/SetupProfile'));
const Moowoo = lazyWithRetry(() => import('./screens/Moowoo/Moowoo'));
const MoowooHistory = lazyWithRetry(() => import('./screens/MoowooHistory/MoowooHistory'));
const Checkout = lazyWithRetry(() => import('./screens/Checkout/Checkout'));
const PaymentResult = lazyWithRetry(() => import('./screens/PaymentResult/PaymentResult'));
const Settings = lazyWithRetry(() => import('./screens/Settings/Settings'));
const NotificationBox = lazyWithRetry(() => import('./screens/NotificationBox/NotificationBox'));
const TestTranslation = lazyWithRetry(() => import('./screens/TestTranslation/TestTranslation'));

import AppLayout from './components/AppLayout';
import GlobalNotificationBanner from './components/GlobalNotificationBanner';
import { HAS_SEEN_INTRO, INVITE_JOIN_IN_PROGRESS } from './constants/storageKeys';
import { initOrResume } from './utils/callAudioGraph';
import { initVersionCheck } from './lib/versionCheck';

const PUBLIC_PATHS = ['/auth', '/onboarding', '/join'];

/**
 * 초대 링크 가입 진행 중 표식 검사
 * 시간 제한은 사용하지 않는다. 표식은 로그아웃 시점에만 회수한다.
 */
function isInviteJoinInProgress() {
  try {
    return Boolean(sessionStorage.getItem(INVITE_JOIN_IN_PROGRESS));
  } catch {
    return false;
  }
}

function AuthGate({ children }) {
  const [session, setSession] = useState(null);
  const [hasProfile, setHasProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { balance, setBalance } = useMoowooContext();
  const { setMyLanguage } = useLanguage();

  // 최초 1회 인증 확인 완료 플래그 및 현재 사용자 ID/언어동기화 상태 추적
  const isInitialAuthCheckedRef = useRef(false);
  const currentUserIdRef = useRef(null);
  const isLangSyncedRef = useRef(false);
  const hasProfileRef = useRef(null);

  const checkAuthAndProfile = async (currentSession, isMounted = () => true, isBackground = false) => {
    if (!currentSession?.user?.id) {
      currentUserIdRef.current = null;
      isLangSyncedRef.current = false;
      hasProfileRef.current = null;
      if (isMounted()) {
        setSession(null);
        setHasProfile(null);
        setLoading(false);
        isInitialAuthCheckedRef.current = true;
      }
      return;
    }

    const userId = currentSession.user.id;
    if (currentUserIdRef.current !== userId) {
      currentUserIdRef.current = userId;
      isLangSyncedRef.current = false;
      hasProfileRef.current = null;
    }

    // [방패 2] 이미 true로 확인된 동일 사용자의 재확인인 경우: 실패, 0건, 오류 중 무엇이든 false나 null로 내리지 않는다.
    const alreadyVerified = hasProfileRef.current === true;

    // [방패 1] 프로필 조회 및 재시도: error가 있으면 최대 3회 재시도 (대기 0.5초 → 1초 → 2초)
    const retryDelays = [500, 1000, 2000];
    let profileData = null;
    let queryError = null;

    for (let attempt = 0; attempt <= retryDelays.length; attempt++) {
      if (!isMounted()) return;

      const res = await supabase
        .from('profiles')
        .select('id, my_language')
        .eq('id', userId)
        .maybeSingle();

      profileData = res.data;
      queryError = res.error;

      if (!queryError) {
        break;
      }

      if (attempt < retryDelays.length) {
        await new Promise((resolve) => setTimeout(resolve, retryDelays[attempt]));
      }
    }

    let determinedHasProfile = null;

    if (!queryError) {
      if (profileData) {
        determinedHasProfile = true;
      } else {
        // [방패 1] error 없이 0건이 오면 바로 '없음'으로 정하지 않는다.
        // supabase.auth.getUser()로 서버에서 로그인을 다시 확인하고,
        // 그 사용자 id가 현재 세션 id와 같을 때만 한 번 더 조회하고, 그래도 0건이면 그때 false(없음 확정)로 정한다.
        try {
          const { data: serverUserData, error: userError } = await supabase.auth.getUser();
          if (!userError && serverUserData?.user?.id === userId) {
            const recheck = await supabase
              .from('profiles')
              .select('id, my_language')
              .eq('id', userId)
              .maybeSingle();

            if (!recheck.error) {
              if (recheck.data) {
                profileData = recheck.data;
                determinedHasProfile = true;
              } else {
                determinedHasProfile = false;
              }
            } else {
              determinedHasProfile = null;
            }
          } else {
            determinedHasProfile = null;
          }
        } catch {
          determinedHasProfile = null;
        }
      }
    } else {
      // 재시도가 모두 실패하면 null(모름)
      determinedHasProfile = null;
    }

    if (isMounted()) {
      if (profileData?.my_language && !isLangSyncedRef.current) {
        setMyLanguage(profileData.my_language);
        isLangSyncedRef.current = true;
      }
      setSession(currentSession);

      // [방패 2] 한 번 true로 확인된 뒤에는 백그라운드 재확인 결과가 실패, 0건, 오류 중 무엇이든 false나 null로 내리지 않는다.
      if (alreadyVerified) {
        setHasProfile(true);
        hasProfileRef.current = true;
      } else if (determinedHasProfile !== null) {
        setHasProfile(determinedHasProfile);
        hasProfileRef.current = determinedHasProfile;
      } else {
        setHasProfile(null);
        hasProfileRef.current = null;
      }

      // 초기 인증 로딩 해제 (재시도 실패 시에도 무한 로딩 방지)
      if (!isBackground || !isInitialAuthCheckedRef.current) {
        setLoading(false);
        isInitialAuthCheckedRef.current = true;
      }
    }
  };

  // [프로필 준비 완료 전역 이벤트 수신] 초대 가입 후 프로필이 DB에 생성되었을 때 hasProfile 상태 즉시 갱신
  useEffect(() => {
    let mounted = true;

    const handleProfileReady = async () => {
      console.log('[초대가입] 프로필 준비 이벤트 수신, 상태 재확인');
      const { data: { session } } = await supabase.auth.getSession();
      if (mounted) {
        checkAuthAndProfile(session, () => mounted, true);
      }
    };

    window.addEventListener('moowoo:profile-ready', handleProfileReady);

    return () => {
      mounted = false;
      window.removeEventListener('moowoo:profile-ready', handleProfileReady);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let mounted = true;

    // 1. 앱 마운트 시 최초 세션 조회 (최초 1회에만 loading 스피너 표시)
    supabase.auth.getSession().then(({ data: { session } }) => {
      checkAuthAndProfile(session, () => mounted, false);
    });

    // 2. 인증 상태 변화 이벤트 구독
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // 1) 로그아웃: 즉시 세션 정리 및 화면 전환 (children 언마운트 허용)
      if (event === 'SIGNED_OUT') {
        // [표식 회수] 같은 탭에서 로그아웃 후 일반 가입을 시도할 때, 남은 표식이 이름 입력 단계를 건너뛰게 만드는 것을 막기 위한 회수다.
        try {
          sessionStorage.removeItem(INVITE_JOIN_IN_PROGRESS);
          console.log('[초대가입] 표식 회수 (로그아웃)');
        } catch {}

        currentUserIdRef.current = null;
        hasProfileRef.current = null; // 로그아웃 시 프로필 판정 기억도 함께 초기화
        isLangSyncedRef.current = false;
        isInitialAuthCheckedRef.current = true;
        if (mounted) {
          setSession(null);
          setHasProfile(null);
          setLoading(false);
        }
        return;
      }

      // 2) 세션 유지/갱신 이벤트 (TOKEN_REFRESHED, USER_UPDATED, INITIAL_SESSION, 동일 사용자 SIGNED_IN)
      const isInitial = !isInitialAuthCheckedRef.current;
      const isSameUser = Boolean(
        currentUserIdRef.current &&
        session?.user?.id &&
        currentUserIdRef.current === session.user.id
      );

      // 이미 최초 검증이 완료된 상태에서 동일 사용자의 세션 갱신/포커스 복귀 이벤트가 발생한 경우:
      // loading을 true로 바꾸지 않고 in-place(조용히) 갱신하여 children 및 DOM(file input 등) 트리 보존
      const isBackground = !isInitial && isSameUser;

      // 신규 사용자로 로그인된 경우에만 필요 시 loading 표시
      if (!isInitial && !isSameUser && session?.user?.id) {
        setLoading(true);
      }

      checkAuthAndProfile(session, () => mounted, isBackground);
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-[#FFFDF5] text-on-surface-variant py-10">
        <span className="material-symbols-outlined text-[36px] mb-2 animate-spin text-primary opacity-80">progress_activity</span>
      </div>
    );
  }

  if (location.pathname === '/') {
    if (session) {
      return <Navigate to="/chat-list" replace />;
    }
    let hasSeenIntro = false;
    try {
      hasSeenIntro = Boolean(localStorage.getItem(HAS_SEEN_INTRO));
    } catch (_) {
      hasSeenIntro = false;
    }
    return hasSeenIntro ? <Navigate to="/auth" replace /> : <Navigate to="/onboarding" replace />;
  }

  if (!session && !PUBLIC_PATHS.includes(location.pathname)) {
    return <Navigate to="/auth" replace />;
  }

  if (session && hasProfile === false && location.pathname !== '/setup-profile' && !PUBLIC_PATHS.includes(location.pathname) && !isInviteJoinInProgress()) {
    return <Navigate to="/setup-profile" replace />;
  }

  return children;
}

function App() {
  useEffect(() => {
    let isExited = false;
    const entryTime = Date.now();
    const sessionId = Math.random().toString(36).substring(2, 7).toUpperCase();
    let userInfoStr = '비회원(익명)';
    
    const sendTelegramMessage = async (text, isExit = false) => {
      if (import.meta.env.DEV) {
        console.log(`로컬 환경: 텔레그램 알림 발송 생략\n[${isExit ? '이탈' : '진입'} 메시지]\n${text}`);
        return;
      }
      
      try {
        await supabase.functions.invoke('telegram-notify', {
          body: { text }
        });
      } catch (err) {
        console.warn('Telegram notification failed:', err);
      }
    };

    const getBrowserInfo = () => {
      const ua = navigator.userAgent;
      let os = 'Unknown';
      if (ua.indexOf('Win') !== -1) os = 'Windows';
      else if (ua.indexOf('Mac') !== -1) os = 'MacOS';
      else if (ua.indexOf('Linux') !== -1) os = 'Linux';
      else if (ua.indexOf('Android') !== -1) os = 'Android';
      else if (ua.indexOf('like Mac') !== -1) os = 'iOS';
      
      let browser = 'Unknown';
      if (ua.indexOf('Firefox') !== -1) browser = 'Firefox';
      else if (ua.indexOf('SamsungBrowser') !== -1) browser = 'Samsung Browser';
      else if (ua.indexOf('Opera') !== -1 || ua.indexOf('OPR') !== -1) browser = 'Opera';
      else if (ua.indexOf('Trident') !== -1) browser = 'IE';
      else if (ua.indexOf('Edge') !== -1 || ua.indexOf('Edg') !== -1) browser = 'Edge';
      else if (ua.indexOf('Chrome') !== -1) browser = 'Chrome';
      else if (ua.indexOf('Safari') !== -1) browser = 'Safari';

      return { os, browser };
    };

    const reportEntry = async () => {
      try {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const name = user.user_metadata?.name || user.user_metadata?.full_name || '이름없음';
            userInfoStr = `${name} / ${user.email}`;
          }
        } catch (e) {}

        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '알 수 없음';
        const lang = navigator.language || '알 수 없음';
        const touch = ('ontouchstart' in window || navigator.maxTouchPoints > 0) ? 'O' : 'X';
        const screen = `${window.innerWidth}x${window.innerHeight}`;
        const isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? '다크' : '라이트';
        const network = navigator.connection?.effectiveType || '알 수 없음';
        const referrer = document.referrer || '직접 유입';
        const currentPath = window.location.pathname;
        const urlParams = window.location.search || '없음';
        const { os, browser } = getBrowserInfo();

        const msg = `🚨 [무우 앱 진입] (세션: #${sessionId})
👤 접속 계정: ${userInfoStr}
🌍 위치(시간대): ${timeZone}
🕒 언어: ${lang}
📱 기기: ${os} (터치 ${touch})
🌐 브라우저: ${browser}
🎨 화면/테마: ${screen} / ${isDark}
📡 네트워크: ${network}
🔗 유입 경로: ${referrer}
🎯 마케팅/주소: ${currentPath} / ${urlParams}`;

        sendTelegramMessage(msg, false);
      } catch (err) {
        // 알림 발송 실패가 앱 렌더링을 막지 않도록 예외 흡수
      }
    };

    const handleExit = () => {
      if (isExited) return;
      isExited = true;
      const durationMs = Date.now() - entryTime;
      const minutes = Math.floor(durationMs / 60000);
      const seconds = Math.floor((durationMs % 60000) / 1000);
      let durationStr = '';
      if (minutes > 0) durationStr += `${minutes}분 `;
      durationStr += `${seconds}초`;

      const msg = `👋 [무우 앱 이탈] 
🔑 세션: #${sessionId}
👤 접속 계정: ${userInfoStr}
⏱️ 총 체류 시간: ${durationStr}`;

      sendTelegramMessage(msg, true);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handleExit();
      }
    };

    const onPageHide = () => {
      handleExit();
    };

    reportEntry();

    window.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      window.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, []);

  // [전역 오디오 warm-up]
  // 브라우저 자동재생 정책상 사용자 제스처 없이는 AudioContext 가 활성화되지 않는다.
  // 앱 진입 후 최초 조작 시점에 미리 활성화해 두어야,
  // 이후 비동기로 도착하는 수신 전화의 벨소리가 즉시 울릴 수 있다.
  useEffect(() => {
    const handleFirstUserGesture = () => {
      initOrResume();
      cleanupListeners();
    };

    const cleanupListeners = () => {
      window.removeEventListener('click', handleFirstUserGesture);
      window.removeEventListener('touchstart', handleFirstUserGesture);
    };

    window.addEventListener('click', handleFirstUserGesture, { once: true, passive: true });
    window.addEventListener('touchstart', handleFirstUserGesture, { once: true, passive: true });

    return () => {
      cleanupListeners();
    };
  }, []);

  // [보안 묶음 2] 오래 켜 둔 앱 화면 복귀 시 조용한 버전 갱신 감시자 시작
  useEffect(() => {
    initVersionCheck();
  }, []);

  return (
    <LanguageProvider>
      <MoowooProvider>
        <Router>
          <AppLayout>
            <AuthGate>
              <NotificationProvider>
                <CallProvider>
                  <GlobalNotificationBanner />
                  <Suspense
                    fallback={
                      <div className="flex-1 flex flex-col items-center justify-center bg-[#FFFDF5] text-on-surface-variant py-10">
                        <span className="material-symbols-outlined text-[36px] mb-2 animate-spin text-primary opacity-80">progress_activity</span>
                      </div>
                    }
                  >
                    <Routes>
                      <Route path="/onboarding" element={<Onboarding />} />
                      <Route path="/chat-list" element={<ChatList />} />
                      <Route path="/group-chats" element={<GroupChatList />} />
                      <Route path="/new-chat" element={<NewChat />} />
                      <Route path="/new-group-chat" element={<NewGroupChat />} />
                      <Route path="/chat/:chatId" element={<ChatConversation />} />
                      <Route path="/group-info/:chatId" element={<GroupChatInfo />} />
                      <Route path="/group-invite/:chatId" element={<GroupChatInvite />} />
                      <Route path="/invite" element={<Invite />} />
                      <Route path="/join" element={<JoinViaLink />} />
                      <Route path="/contacts" element={<Contacts />} />
                      <Route path="/find-friend" element={<FindFriend />} />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/profile/:userId" element={<FriendProfile />} />
                      <Route path="/avatar" element={<AvatarPicker />} />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/setup-profile" element={<SetupProfile />} />
                      <Route path="/moowoo" element={<Moowoo />} />
                      <Route path="/history" element={<MoowooHistory />} />
                      <Route path="/checkout" element={<Checkout />} />
                      <Route path="/payment/result/:status" element={<PaymentResult />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/notifications" element={<NotificationBox />} />
                      <Route path="/admin/live-test" element={<TestTranslation />} />
                    </Routes>
                  </Suspense>
                </CallProvider>
              </NotificationProvider>
            </AuthGate>
          </AppLayout>
        </Router>
      </MoowooProvider>
    </LanguageProvider>
  );
}

export default App;
